import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, User, ArrowLeft, Loader2, Tag, ExternalLink } from 'lucide-react';
import api from '../api';

const CATEGORY_LABELS = {
  technology: 'Technology',
  maintenance: 'Maintenance',
  industry: 'Industry News',
  'case-studies': 'Project Highlights',
  tips: 'Tips & Guides',
};

function articleDate(article) {
  const d = article?.publishedAt || article?.date;
  return d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
}

const UpdateDetailPage = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/blog', { params: { id } });
        if (!cancelled) setArticle(data);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <p className="text-gray-600 text-lg mb-6">This update is no longer available or has expired.</p>
        <Link to="/updates" className="text-blue-600 font-semibold hover:underline flex items-center">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Industry Updates
        </Link>
      </div>
    );
  }

  const paragraphs = (article.content || article.excerpt || '').split(/\n\n+/).filter(Boolean);

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <div className="relative h-56 sm:h-72 md:h-96">
        <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-blue-900/30" />
      </div>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-10"
        >
          <Link
            to="/updates"
            className="inline-flex items-center text-blue-600 text-sm font-medium hover:underline mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> All Updates
          </Link>

          <div className="flex flex-wrap gap-2 mb-4">
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
              {CATEGORY_LABELS[article.category] || article.category}
            </span>
            {article.featured && (
              <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                Featured
              </span>
            )}
            {article.isAutoFetched && (
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                Auto-curated
              </span>
            )}
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
            {article.title}
          </h1>

          <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-gray-500 mb-8 pb-8 border-b border-gray-100">
            <span className="flex items-center"><User className="w-4 h-4 mr-1" />{article.author}</span>
            <span className="flex items-center"><Calendar className="w-4 h-4 mr-1" />{articleDate(article)}</span>
            <span className="flex items-center"><Clock className="w-4 h-4 mr-1" />{article.readTime}</span>
          </div>

          <div className="prose prose-lg max-w-none text-gray-700 space-y-5">
            {paragraphs.map((para, i) => (
              <p key={i} className="leading-relaxed">{para}</p>
            ))}
          </div>

          {article.sourceUrl && (
            <a
              href={article.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center mt-8 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Read full article at {article.sourceName || 'source'}
              <ExternalLink className="w-4 h-4 ml-2" />
            </a>
          )}

          {article.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-gray-100">
              <Tag className="w-4 h-4 text-gray-400 mt-1" />
              {article.tags.map((tag) => (
                <span key={tag} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                  {tag}
                </span>
              ))}
            </div>
          )}

          <p className="text-xs text-gray-400 mt-8">
            Industry updates are refreshed regularly and may be removed after 90 days.
          </p>
        </motion.div>
      </article>
    </div>
  );
};

export default UpdateDetailPage;
